// src/store/slices/loyaltySlice.ts
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import loyaltyService from '../../services/loyaltyService';

interface LoyaltyTransaction {
  id: string;
  userId: string;
  type: 'earn' | 'redeem';
  points: number;
  appointmentId?: string;
  serviceDetails?: {
    name: string;
    price: number;
  };
  stylistId?: string;
  stylistName?: string;
  createdAt: string;
}

interface LoyaltyBreakdown {
  stylist: {
    _id: string;
    name: string;
    profilePhoto?: string;
  };
  totalPoints: number;
  availablePoints: number;
  usedPoints: number;
}

interface LoyaltyState {
  totalPoints: number;
  availablePoints: number;
  transactions: LoyaltyTransaction[];
  breakdown: LoyaltyBreakdown[];
  isLoading: boolean;
  error: string | null;
}

const initialState: LoyaltyState = {
  totalPoints: 0,
  availablePoints: 0,
  transactions: [],
  breakdown: [],
  isLoading: false,
  error: null,
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const mapTransactions = (raw: any[]): LoyaltyTransaction[] =>
  raw.map((t: any, index: number) => ({
    id: t._id ?? t.id ?? `tx-${index}`,  // deterministic fallback (I2 fix)
    userId: t.user || '',
    type: t.type === 'redeem' ? 'redeem' : 'earn',
    points: t.points ?? 0,
    appointmentId: t.appointment,
    // Map serviceDetails if backend returns it (I1 fix)
    serviceDetails: t.serviceDetails ?? t.service ?? undefined,
    stylistId: t.stylist?._id ?? t.stylist,
    stylistName: t.stylist?.name,
    createdAt: t.createdAt || new Date().toISOString(),
  }));

const mapBreakdown = (raw: any[]): LoyaltyBreakdown[] =>
  raw.map((b: any) => ({
    stylist: b.stylist || { _id: '', name: 'Unknown' },
    totalPoints: b.availablePoints ?? 0,
    availablePoints: b.availablePoints ?? 0,
    usedPoints: 0,
  }));

// ─── Async thunks ─────────────────────────────────────────────────────────────

/**
 * Fetches loyalty info AND transactions in a single API call.
 * C3: Removed the duplicate fetchLoyaltyTransactions call — both slices of
 * data come from the same /loyalty endpoint.
 */
export const fetchLoyaltyInfo = createAsyncThunk(
  'loyalty/fetchInfo',
  async (_, { rejectWithValue }) => {
    try {
      const response = await loyaltyService.getLoyalty();
      const breakdown = mapBreakdown(response.breakdown || []);
      return {
        totalPoints: response.loyaltyPoints ?? 0,
        // C2 fix: availablePoints is the sum of per-stylist available points,
        // not the raw loyaltyPoints balance.
        availablePoints: breakdown.reduce((sum, b) => sum + b.availablePoints, 0),
        breakdown,
        transactions: mapTransactions(response.transactions || []),
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch loyalty info');
    }
  }
);

/**
 * Kept for backward-compatibility with any dispatch calls still using it,
 * but now delegates to fetchLoyaltyInfo to avoid a duplicate API call (C3).
 */
export const fetchLoyaltyTransactions = createAsyncThunk(
  'loyalty/fetchTransactions',
  async (_, { dispatch, rejectWithValue }) => {
    try {
      const result = await dispatch(fetchLoyaltyInfo()).unwrap();
      return result.transactions;
    } catch (error: any) {
      return rejectWithValue(error?.message || 'Failed to fetch loyalty transactions');
    }
  }
);

export const redeemLoyaltyPoints = createAsyncThunk(
  'loyalty/redeemPoints',
  async ({
    points,
    servicePrice,
    stylistId
  }: {
    points: number;
    servicePrice: number;
    stylistId: string;
  }, { rejectWithValue }) => {
    try {
      const result = await loyaltyService.redeemPoints(points, servicePrice, stylistId);
      return result;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to redeem loyalty points');
    }
  }
);

// ─── Slice ────────────────────────────────────────────────────────────────────

const loyaltySlice = createSlice({
  name: 'loyalty',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // fetchLoyaltyInfo
      .addCase(fetchLoyaltyInfo.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLoyaltyInfo.fulfilled, (state, action) => {
        state.isLoading = false;
        state.totalPoints = action.payload.totalPoints;
        state.availablePoints = action.payload.availablePoints;
        state.breakdown = action.payload.breakdown;
        state.transactions = action.payload.transactions;
      })
      .addCase(fetchLoyaltyInfo.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // fetchLoyaltyTransactions (delegates to fetchLoyaltyInfo internally)
      .addCase(fetchLoyaltyTransactions.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchLoyaltyTransactions.fulfilled, (state, action: PayloadAction<LoyaltyTransaction[]>) => {
        state.isLoading = false;
        state.transactions = action.payload;
      })
      .addCase(fetchLoyaltyTransactions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // redeemLoyaltyPoints
      .addCase(redeemLoyaltyPoints.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(redeemLoyaltyPoints.fulfilled, (state, action) => {
        state.isLoading = false;
        const pointsRedeemed =
          action.payload.pointsRedeemed ??
          action.payload.transaction?.points ??
          0;
        state.availablePoints = Math.max(0, state.availablePoints - pointsRedeemed);
        if (typeof action.payload.loyaltyPoints === 'number') {
          state.totalPoints = action.payload.loyaltyPoints;
        }

        const stylistId = action.payload.stylistId || action.payload.transaction?.stylist;
        const breakdownIndex = state.breakdown.findIndex(
          b => b.stylist?._id === stylistId || b.stylist?._id?.toString() === stylistId?.toString()
        );
        if (breakdownIndex !== -1 && pointsRedeemed) {
          state.breakdown[breakdownIndex].availablePoints = Math.max(
            0,
            state.breakdown[breakdownIndex].availablePoints - pointsRedeemed
          );
          state.breakdown[breakdownIndex].usedPoints =
            (state.breakdown[breakdownIndex].usedPoints || 0) + pointsRedeemed;
        }
      })
      .addCase(redeemLoyaltyPoints.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export default loyaltySlice.reducer;
